import type { BeggsBrillFlowRegimeProperties } from ".";
import { BeggsBrillFlowRegimes } from ".";

// ----------------------------   HELPERS
function getTheta(C: number, phiRad: number): number {
  // =1+E223*(SIN(1.8*D8)-0.333*(SIN(1.8*D8))^2)
  return 1 + C * (Math.sin(1.8 * phiRad) - 0.333 * Math.sin(1.8 * phiRad) ** 2);
}

function getC(
  CL: number,
  d: number,
  e: number,
  f: number,
  g: number,
  N_lv: number,
  Fr: number
): number {
  try {
    const result = Math.max(
      (1 - CL) *
        Math.log(d * Math.pow(CL, e) * Math.pow(N_lv, f) * Math.pow(Fr, g)),
      0
    );
    if (isNaN(result) || !isFinite(result)) {
      return 0;
    }
    return result;
  } catch (error) {
    return 0;
  }
}

export function getActualHoldupAlpha(
  noSlipLiquidHoldupLambdaCL: number,
  areg: number,
  breg: number,
  creg: number,
  froudeNumber: number
): number {
  // D214: =MAX(D210*((D153^D211)/(D174^D212)),D153)
  return Math.max(
    areg * (noSlipLiquidHoldupLambdaCL ** breg / froudeNumber ** creg),
    noSlipLiquidHoldupLambdaCL
  );
}
// ----------------------------   HELPERS

const flowRegimeValues: BeggsBrillFlowRegimeProperties = {
  Segregated: {
    areg: 0.98,
    breg: 0.4846,
    creg: 0.0868,
    d: 0.011,
    e: -3.768,
    f: 3.539,
    g: -1.614
  },
  Intermittent: {
    areg: 0.845,
    breg: 0.5351,
    creg: 0.0173,
    d: 2.96,
    e: 0.305,
    f: -0.4473,
    g: 0.0978
  },
  Distributed: {
    areg: 1.065,
    breg: 0.5824,
    creg: 0.0609,
    d: 0,
    e: 0,
    f: 0,
    g: 0
  },
  Downhill: {
    areg: 0,
    breg: 0,
    creg: 0,
    d: 4.7,
    e: -0.3962,
    f: 0.1244,
    g: -0.5056
  }
};

function getFlowRegimeCoefficients(
  flowRegime: BeggsBrillFlowRegimes
): BeggsBrillFlowRegimeProperties[keyof BeggsBrillFlowRegimeProperties] | null {
  if (
    flowRegime === BeggsBrillFlowRegimes.SEGREGATED ||
    flowRegime === BeggsBrillFlowRegimes.INTERMITTENT ||
    flowRegime === BeggsBrillFlowRegimes.DISTRIBUTED ||
    flowRegime === BeggsBrillFlowRegimes.DOWNHILL
  ) {
    return flowRegimeValues[flowRegime];
  }
  return null;
}

export function getAlphaLiquid(
  liquidSuperficialVelocityFTSEC: number,
  liquidDensityLBMFT3: number,
  liquidInterfacialTension: number,
  noSlipLiquidHoldupLambdaCL: number,
  froudeNumber: number,
  phiRad: number,
  flowRegime: BeggsBrillFlowRegimes,
  frTrans: number[]
): {
  liquidAlpha: number;
  intermediates?: {
    [key: string]: number;
  };
} {
  if (flowRegime === BeggsBrillFlowRegimes.TRANSITION) {
    // D206: =IF(D181=5,(D178-D174)/(D178-D177),0)
    const trA = (frTrans[2] - froudeNumber) / (frTrans[2] - frTrans[1]);
    // D207: =1-D206
    const trB = 1 - trA;

    // D221: =1.938*D150*(D63/D169)^(1/4)
    const nlv =
      1.938 *
      liquidSuperficialVelocityFTSEC *
      (liquidDensityLBMFT3 / liquidInterfacialTension) ** 0.25;

    // D214: =MAX(D210*((D153^D211)/(D174^D212)),D153)
    const segregatedActualHoldupAlpha = getActualHoldupAlpha(
      noSlipLiquidHoldupLambdaCL,
      flowRegimeValues.Segregated.areg,
      flowRegimeValues.Intermittent.breg,
      flowRegimeValues.Intermittent.creg,
      froudeNumber
    );

    // D223: =MAX((1-D153)*LN(D216*(D153^D217))*(D221^D218)*(D174^D219),0)
    const segregatedC = getC(
      noSlipLiquidHoldupLambdaCL,
      flowRegimeValues.Segregated.d,
      flowRegimeValues.Segregated.e,
      flowRegimeValues.Segregated.f,
      flowRegimeValues.Segregated.g,
      nlv,
      froudeNumber
    );
    // D225: =1+D223*(SIN(1.8*D7)-0.333*(SIN(1.8*D7))^2)
    const segregatedTheta = getTheta(segregatedC, phiRad);
    // D227: =D214*D225
    const segregatedAlphaLiquid = segregatedActualHoldupAlpha * segregatedTheta;

    // E214: =MAX(E210*((D153^E211)/(D174^E212)),D153)
    const intermittentActualHoldupAlpha = getActualHoldupAlpha(
      noSlipLiquidHoldupLambdaCL,
      flowRegimeValues.Intermittent.areg,
      flowRegimeValues.Intermittent.breg,
      flowRegimeValues.Intermittent.creg,
      froudeNumber
    );

    // E216: =MAX((1-D153)*LN(E216*(D153^E217))*(E221^E218)*(D174^E219),0)
    const intermittentC = getC(
      noSlipLiquidHoldupLambdaCL,
      flowRegimeValues.Intermittent.d,
      flowRegimeValues.Intermittent.e,
      flowRegimeValues.Intermittent.f,
      flowRegimeValues.Intermittent.g,
      nlv,
      froudeNumber
    );
    // E225: =1+E223*(SIN(1.8*D8)-0.333*(SIN(1.8*D8))^2)
    const intermittentTheta = getTheta(intermittentC, phiRad);
    // E227: =E214*E225
    const intermittentAlphaLiquid =
      intermittentActualHoldupAlpha * intermittentTheta;

    // D229: =D206*D227+D207*E227
    const liquidAlpha = Math.min(
      trA * segregatedAlphaLiquid + trB * intermittentAlphaLiquid,
      1
    );

    return {
      liquidAlpha,
      intermediates: {
        trA,
        trB,
        nlv,
        segregatedActualHoldupAlpha,
        segregatedC,
        segregatedTheta,
        segregatedAlphaLiquid,
        intermittentActualHoldupAlpha,
        intermittentC,
        intermittentTheta,
        intermittentAlphaLiquid
      }
    };
  } else if (flowRegime !== BeggsBrillFlowRegimes.LIQUID) {
    const flowRegimeCoefficients = getFlowRegimeCoefficients(flowRegime);
    if (!flowRegimeCoefficients) {
      return {
        liquidAlpha: Math.min(noSlipLiquidHoldupLambdaCL, 1)
      };
    }

    // D196
    const nlv = 10.6;

    // D189: =IF(D181=1,D153,MAX(D185*((D153^D186)/(D174^D187)),D153))
    const actualHoldupAlpha = getActualHoldupAlpha(
      noSlipLiquidHoldupLambdaCL,
      flowRegimeCoefficients.areg || 0,
      flowRegimeCoefficients.breg || 0,
      flowRegimeCoefficients.creg || 0,
      froudeNumber
    );

    // D198: =IFERROR(MAX((1-D153)*LN(D191*(D153^D192))*(D196^D193)*(D174^D194),0),0)
    const C = getC(
      noSlipLiquidHoldupLambdaCL,
      flowRegimeCoefficients.d || 0,
      flowRegimeCoefficients.e || 0,
      flowRegimeCoefficients.f || 0,
      flowRegimeCoefficients.g || 0,
      nlv,
      froudeNumber
    );

    // D200: =1+D198*(SIN(1.8*D8)-0.333*(SIN(1.8*D8))^2)
    const theta = getTheta(C, phiRad);

    // D202: =D189*D200
    const liquidAlpha = Math.min(actualHoldupAlpha * theta, 1);

    return {
      liquidAlpha,
      intermediates: {
        actualHoldupAlpha,
        noSlipLiquidHoldupLambdaCL,
        phiRad
      }
    };
  } else {
    return {
      liquidAlpha: Math.min(noSlipLiquidHoldupLambdaCL, 1)
    };
  }
}
